import React, { Fragment } from "react";
import styled from "styled-components";
import PropTypes from "prop-types";
import Scrollbars from "react-custom-scrollbars";
import Button from "./button";
import IconButton from "./icon_button";
import QuantityEditor from "./quantity_editor";
import StatusTooltip from "./status_tooltip";
import Chip from "./chip";
import SubAccessoriesManager from "./subaccessories_manager";
import NotificationFeed from "./notification_feed";
import Skeleton from "./skeleton";
import SlotCapacities from "./slot_capactities";
import SlotChip from "./slot_chip";
import Tooltip from "./tooltip";
import ModalNotification from "./modal_notification";
import MsxIcons from "../../../features/msx-icons";

const ModalContainer = styled.div`
  display: flex;
  position: fixed;
  top: 0;
  right: 0;
  bottom: 0;
  left: 0;
  background-color: rgba(51, 51, 51, 0.5);
  justify-content: center;
  align-items: center;
  z-index: 9999;
`;

// I don't really know why the pixel density stuff is working, but it works :)
// This is needed to scale the modal in case there are some system zoom (150% on windows for instance).
const Container = styled.div`
  border-radius: 10px;
  position: relative;
  align-self: center;
  display: flex;
  flex-direction: column;
  max-width: 1020px;
  width: 90%;
  height: calc(100vh - 4em);
  background-color: white;
  color: #333333;
  padding-top: 10px;
  overflow-x: hidden;

  @media (max-width: ${(props) => props.mobileBreakpoint ? props.mobileBreakpoint : 800}px) {
    width: 95%;
  }

  @media (-webkit-min-device-pixel-ratio: 1.35) {
    transform: scale(.7);
    height: calc((100vh * 1.3));
    width: 130%;
    max-width: calc(1020px * 1.3);
    box-sizing: border-box;
    margin-right: calc(-50% * 0.3);
    margin-left: calc(-50% * 0.3);
  }

  @media (-webkit-min-device-pixel-ratio: 1.70) {
    transform: scale(.6);
    height: calc((100vh * 1.6));
    width: 160%;
    max-width: calc(1020px * 1.6);
    box-sizing: border-box;
    margin-right: calc(-50% * 0.6);
    margin-left: calc(-50% * 0.6);
  }
`;

const HeadWrapper = styled.div`
  display: flex;
  flex-direction: column;
  @media (max-width: ${(props) => props.mobileBreakpoint ? props.mobileBreakpoint : 800}px) {
    padding: 0px 20px;
  }
`;

const ImageAndHeadWrapper = styled.div`
  display: flex;
`;

const TitleAndCloseWrapper = styled.div`
  display: flex;
  justify-content: space-between;
`;

const TitleWrapper = styled.div`
  display: inline-flex;
  align-items: center;
  font-weight: 700;
  @media (max-width: ${(props) => props.mobileBreakpoint ? props.mobileBreakpoint : 800}px) {
    flex-wrap: wrap;
  }
`;

const TitleType = styled.span`
  color: #626469;
  font-size: 16px;
`;

const CloseIcon = styled.span`
  display: inline-block;
  color: #626469;
  cursor: pointer;
`;

const TitleLinkWrapper = styled.div`
  margin: 0px 5px;
`;

const CategoryTabsWrapper = styled.div`
  display: inline-flex;
  margin-top: 10px;
  box-sizing: border-box;
  & :first-child {
    margin-left: -1px;
  }

  @media (max-width: ${(props) => props.mobileBreakpoint ? props.mobileBreakpoint : 800}px) {
    margin-right: -20px;
    margin-left: -20px;
  }
`;

const CategoryTab = styled.div`
  position: relative;
  display: inline-flex;
  flex: 1 0 0px;
  padding: 20px;
  justify-content: center;
  align-items: center;
  border: 1px solid #CCCCCC;
  cursor: pointer;
  margin-right: -1px;
  font-size: 18px;
  color: #626469;

  ${props => props.active ? `border-bottom: none; border-top: 4px solid ${props.secondaryColor || "#008a16"};border-bottom: none;background-color: #F4F4F4;color: ${props.secondaryColor || "#008a16"};` : ''}
  ${props => props.disabled ? `opacity: 0.5;` : ''}
  ${props => (props.preventNavigation || props.disabled) && `pointer-events: none;`}

  @media (max-width: ${(props) => props.mobileBreakpoint ? props.mobileBreakpoint : 800}px) {
    padding: 10px;
    font-size: 16px;
  }
`;

const TabLabel = styled.span`
  font-style: normal;
  font-weight: 700;
  text-align: center;
  @media (max-width: ${(props) => props.mobileBreakpoint ? props.mobileBreakpoint : 800}px) {
    text-overflow: ellipsis;
    overflow: hidden;
  }
`;

const ContentWrapper = styled.div`
  position: relative;
  display: flex;
  flex-direction: column;
  flex: 1 1 0px;
  padding: 20px 12px 0px 40px;
  background-color: #F4F4F4;
  border-top: 1px solid #CCC;
  border-bottom: 1px solid #CCC;
  margin-top: -1px;
  @media (max-width: ${(props) => props.mobileBreakpoint ? props.mobileBreakpoint : 800}px) {
    padding: 10px 2px 0px 20px;
  }
`;

const ButtonWrapper = styled.div`
  display: flex;
  justify-content: center;
  margin-top: 10px;
  padding-bottom: 10px;
`;

const ScrollbarsContent = styled.div`
  display: flex;
  padding-right: 16px;
  flex-direction: column;
`;

const OptionsContent = styled.div`
  display: flex;
  flex-wrap: wrap;
  @media (max-width: ${(props) => props.mobileBreakpoint ? props.mobileBreakpoint : 800}px) {
    flex-direction: column;
  }
`;

const TopBorder = styled.div`
  position: absolute;
  top: 0px;
  left: 0px;
  right: 0px;
  height: 3px;
  border-top-right-radius: 8px;
  border-top-left-radius: 8px;
  background-color: white;
  transition: background-color 250ms, box-shadow 250ms;
`;

const CategoryWrapper = styled.div`
  position: relative;
  box-sizing: border-box;
  display: flex;
  flex-direction: column;
  flex: 0 0 calc(50% - 10px);
  padding: 24px;
  margin-bottom: 15px;
  margin-right: 10px;

  background: #FFFFFF;
  border: 1px solid rgba(160, 164, 168, 0.4);
  border-radius: 4px;
  cursor: pointer;
  transition: box-shadow 250ms;

  ${props => props.disabled && 'opacity 0.75; pointer-events: none;'}

  @media (max-width: ${(props) => props.mobileBreakpoint ? props.mobileBreakpoint : 800}px) {
    flex: 1 1 0px;
    margin-right: 0px;
  }

  :hover {
    ${TopBorder} {
      background-color: ${props => props.color || '#008a16' };
    }
    box-shadow: 0px 20px 20px -20px rgba(81, 81, 81, 0.72);
  }
`;

const CategoryTitle = styled.div`
  font-weight: 700;
  font-size: 18px;
  color: #2F3136;
  margin-right: 10px;
`;

const CategoryDescriptionAndActionsWrapper = styled.div`
  display: flex;
  flex-wrap: wrap;
  justify-content: flex-end;
  flex: 1 1 0px;
`;

const CategoryDescription = styled.div`
  font-weight: 400;
  font-size: 16px;
  color: #626469;
  margin-top: 10px;
`;

const BreadcrumbWrapper = styled.div`
  display: flex;
  margin-top: 10px;
  align-items: center;

  @media (max-width: ${(props) => props.mobileBreakpoint ? props.mobileBreakpoint : 800}px) {
    display: none;
  }
`;

const BreadcrumbLink = styled.span`
  font-weight: 400;
  font-size: 16px;
  color: #0F0F0F;

  ${props => props.disabled && `pointer-events: none;`}

  ${props => props.isLast ? `color: #626469;` : `
    cursor: pointer;
    :hover {
      text-decoration: underline;
    }
  `}
`;

const BreadcrumSeparator = styled.span`
  margin: 0px 10px;
`;

const BreadcrumbItem = styled.span`
  display: flex;
  align-items: center;
  @media (max-width: ${(props) => props.mobileBreakpoint ? props.mobileBreakpoint : 800}px) {
      display: none;
    }
`;

const OptionWrapper = styled.div`
  position: relative;
  box-sizing: border-box;
  display: flex;
  flex-direction: column;
  flex: 0 0 calc(100%);
  max-width: calc(100%);
  padding: 24px 24px 24px 10px;
  margin-bottom: 15px;
  margin-right: 10px;

  background: #FFFFFF;
  border: 1px solid rgba(160, 164, 168, 0.4);
  border-radius: 4px;

  ${props => props.disabled && 'opacity 0.75; pointer-events: none;'}

  @media (max-width: ${(props) => props.mobileBreakpoint ? props.mobileBreakpoint : 800}px) {
    flex: 1 1 0px;
    margin-right: 0px;
  }
`;

const OptionContent = styled.div`
  display: flex;
`;

const SubAccessoriesWrapper = styled.div`
  display: flex;
  flex-direction: column;
  flex: 1 1 0px;
  margin-top: 5px;
  margin-left: -10px;
  margin-right: -24px;
  margin-bottom: -24px;
`;

const OptionCol = styled.div`
  display: flex;
  flex: 1 1 0px;
  flex-direction: column;
`;

const OptionRow = styled.div`
  display: flex;
  margin-left: 5px;
  justify-content: space-between;
  align-items: center;
`;

const OptionLinkWrapper = styled.div`
  display: inline-flex;
  flex-wrap: wrap;
  align-items: center;
  font-size: 14px;
  font-weight: 700;
`;

const OptionImage = styled.img`
  width: 80px;
  height: 80px;
  margin-right: 10px;
  @media (max-width: 400px) {
    width: 50px;
    height: 50px;
  }
`;

const DescriptionWrapper = styled.div`
  display: flex;
  margin-top: 5px;
`;

const OptionDescription = styled.span`
  font-weight: 400;
  font-size: 12px;
  color: #626469;
  -webkit-line-clamp: 2;
  overflow: hidden;
  -webkit-box-orient: vertical;
  display: -webkit-box;
`;

const ActionButtonWrapper = styled.div`
  display: flex;
  flex: 1 1 0px;
  justify-content: flex-end;
`;

const LinkWrapper = styled.div`
  margin-right: 10px;
`;

const NumberOfAccessoriesWrapper = styled.div`
  display: inline-flex;
  margin-top: 10px;
`;

const OptionCategoryMessageWrapper = styled.div`
  display: flex;
  justify-content: flex-end;
  align-items: center;
  margin-top: -15px;
  margin-right: -14px;
  min-height: 26px;
`;

const ContentCategoryTitle = styled.span`
  color: #2F3136;
  font-size: 18px;
  font-weight: 700;
  margin-bottom: 10px;
`;

const SelectOptionTypeTitle = styled.span`
  color: #2F3136;
  font-size: 16px;
  font-weight: 700;
  margin-bottom: 10px;
`;

const OptionTypesWrapper = styled.div`
  display: flex;
  margin-bottom: 10px;
  min-height: 35px;
  max-height: 35px;
  height: 35px;
  margin-right: 26px;
  @media (max-width: ${(props) => props.mobileBreakpoint ? props.mobileBreakpoint : 800}px) {
    margin-right: 16px;
  }
`;

const ChipWrapper = styled.div`
  margin-right: 5px;
`;

const WarningLabel = styled.span`
  font-size: 12px;
  font-weight: 400;
  -webkit-line-clamp: 2;
  overflow: hidden;
  -webkit-box-orient: vertical;
  display: -webkit-box;
`;

const WarningIconWrapper = styled.div`
  display: flex;
  min-width: 24px;
  max-width: 24px;
  min-height: 24px;
  max-height: 24px;
  padding: 2px;
  color: #B10043;
  box-sizing: border-box;
`;

const OptionSlotsWrapper = styled.div`
  display: flex;
  flex-direction: column;
`;

const SlotsTitleAndLineWrapper = styled.div`
  display: flex;
  align-items: center;
`;

const OptionSlotsLabel = styled.span`
  color: #0F0F0F;
  font-size: 12px;
  font-weight: 400;
  margin-right: 10px;
`;

const OptionSlotsLine = styled.div`
  display: flex;
  flex: 1 1 0px;
  background: rgba(0, 0, 0, 0.10);
  height: 1px;
`;

const SlotRow = styled.div`
  display: flex;
  flex-wrap: wrap;
`;

const SlotWrapper = styled.div`
  margin-top: 5px;
  margin-right: 5px;
`;

const CategoryTitleAndStatusWrapper = styled.div`
  display: flex;
  align-items: center;
  flex-wrap: wrap;
`;

const Col = styled.div`
  display: flex;
  flex-direction: column;
  flex: 1 1 0px;    
`;

const ItemImage = styled.img`
  box-sizing: content-box;
  min-width: 80px;
  max-width: 80px;
  width: 80px;
  min-height: 80px;
  max-height: 80px;
  height: 80px;
  margin-right: 10px;

  @media (max-width: 500px) {
    display: none;
  }
`;

const TooltipContent = styled.div`
  display: flex;
  padding: 16px;
  width: 200px;
  border-radius: 4px;

`;

const TooltipLabel = styled.span`
  color: #000;
  font-size: 16px;
  font-weight: 400;
  white-space: pre-wrap;
`;

/**
 * Both of these notifications wrapper will not have position relative on purpose.
 */
const DesktopNotifcationWrapper = styled.div`
  display: none;
  @media (max-width: 500px) {
    display: block;
  }
`;

const MobileNotificationWrapper = styled.div`
  @media (max-width: 500px) {
    display: none;
  }
`;

export const active_category_type = {
  accessories: 1,
  services: 2
}

const active_category_to_label = {
  [active_category_type.accessories]: 'options_screen.accessories',
  [active_category_type.services]: 'options_screen.services'
}

const HeadContainerWrapper = styled.div`
  padding: 0px 16px;
`;

class OptionsModal extends React.Component {
  static propTypes = {
    getLabel: PropTypes.func,
    getAndReplaceLabel: PropTypes.func,
    titleType: PropTypes.string,
    moduleId: PropTypes.string,
    mobileBreakpoint: PropTypes.number,
    getOptions: PropTypes.func,
    closeModal: PropTypes.func,
    imageUrl: PropTypes.string,
    primaryColor: PropTypes.string,
    primaryTextColor: PropTypes.string,
    secondaryColor: PropTypes.string,
    secondaryTextColor: PropTypes.string,
    renderProductLink: PropTypes.func,
    defaultActiveCategoryType: PropTypes.number,
    defaultModalAccessoryToView: PropTypes.object,
    defaultOptionTypes: PropTypes.array,
    onAddOptions: PropTypes.func,
    onRemoveOptions: PropTypes.func,
    onDecreaseOptions: PropTypes.func,
    onAddSubAccessories: PropTypes.func,
    onDecreaseSubAccessories: PropTypes.func,
    onRemoveSubAccessories: PropTypes.func,
    deviceIdentification: PropTypes.object,
    isSaving: PropTypes.bool,
    statusToFilter: PropTypes.string,
    disablePreviousOnEdit: PropTypes.bool
  };

  constructor(props) {
    super(props);
    this.state = {
      activeCategory: props.defaultActiveCategoryType || active_category_type.accessories,
      optionCategories: {},
      breadcrumb: [],
      optionsLoading: false,
      selectedOptionTypes: {}
    };
    this._isMounted = true;
  }

  componentDidMount = () => {
    this._isMounted = true;
    this.retrieveOptions(true);
  };

  optionsToExplore = (optionCategories) => {
    let optionsToExplore = optionCategories.accessories || [];

    if (this.state.activeCategory === active_category_type.services) {
      optionsToExplore = optionCategories.services || [];
    }

    return optionsToExplore;
  }

  /**
   *
   * @returns map containing all the visible IDs
   */
  extractVisibleOptionIds = () => {
    const visibleOptionIds = {};
    const stack = [];

    const optionsToExplore = this.optionsToExplore(this.state.optionCategories);

    // First step to put the initial elements to explore
    for (const optionCategory of optionsToExplore) {
      stack.push({optionCategory});
    }

    // Unfold the stack
    while (stack.length > 0) {
      const stackItem = stack.pop();
      if (stackItem.optionCategory?.isFinal) {
        for (const option of (stackItem.optionCategory.options || [])) {
          visibleOptionIds[option.optionIdentification?.id] = true;
        }
      } else {
        for (const optCategoryKey of Object.keys(stackItem.optionCategory.optionCategories)) {
          const optionCategory = stackItem.optionCategory.optionCategories[optCategoryKey];
          stack.push({optionCategory});
        }
      }
    }

    return visibleOptionIds;
  }

  computeBreadcrumbForDefaultOptionToView = (optionCategories, optionId) => {
    const breadcrumbStack = [];

    const optionsToExplore = this.optionsToExplore(optionCategories);

    // First step to put the initial elements to explore
    for (const optionCategory of optionsToExplore) {
      breadcrumbStack.push({breadcrumb: [], optionCategory});
    }

    // Unfold the stack
    while (breadcrumbStack.length > 0) {
      const stackItem = breadcrumbStack.pop();
      if (stackItem.optionCategory?.isFinal) {
        for (const option of (stackItem.optionCategory.options || [])) {
          if (option.optionIdentification?.id === optionId) {
            return stackItem.breadcrumb;
          }
        }
      } else {
        for (const optCategoryKey of Object.keys(stackItem.optionCategory.optionCategories)) {
          const optionCategory = stackItem.optionCategory.optionCategories[optCategoryKey];
          breadcrumbStack.push({breadcrumb: [...stackItem.breadcrumb, {key: optionCategory.categoryKey, label: optionCategory.categoryLabel}], optionCategory});
        }
      }
    }

    return [];
  }

  extractSlots = () => {
    const stack = [];
    const slots = {};
    const visibleOptionIds = this.extractVisibleOptionIds();

    Object.keys(this.state.optionCategories).forEach(optionCategoryKey => {
      const optCategoryContent = this.state.optionCategories[optionCategoryKey];
      optCategoryContent.forEach((optCategory) => {
        Object.values(optCategory.optionCategories || {}).forEach((opt) => {
          stack.push({breadcrumb: [], optionCategory: opt, optionCategoryKey });
        });
      });
    });

    // Unfold the stack
    while (stack.length > 0) {
      const stackItem = stack.pop();
      if (stackItem.optionCategory?.isFinal) {
        for (const option of (stackItem.optionCategory.options || [])) {
          (option.slotsConsumed || []).forEach(slot => {
            if (slots[slot.slotKey] == null) {
              slots[slot.slotKey] = {...slot}; // Copy of the slot object !
            }

            if (slot.filled) {
              slots[slot.slotKey].filled = true;
            }

            if (visibleOptionIds[option.optionIdentification?.id]) {
              slots[slot.slotKey].visible = true;
            }
          });
        }
      } else {
        for (const optCategoryKey of Object.keys(stackItem.optionCategory.optionCategories)) {
          const optionCategory = stackItem.optionCategory.optionCategories[optCategoryKey];
          stack.push({breadcrumb: [...stackItem.breadcrumb, {key: optionCategory.categoryKey, label: optionCategory.categoryLabel}], optionCategory, optionCategoryKey: stackItem.optionCategoryKey});
        }
      }
    }

    return Object.values(slots);
  }

  addOptions = (deviceIdentification, optionIdentification, quantity) => {
    return Promise.resolve(this.props.onAddOptions(deviceIdentification, optionIdentification, quantity));
  }

  removeOptions = (deviceIdentification, optionIdentification) => {
    return Promise.resolve(this.props.onRemoveOptions(deviceIdentification, optionIdentification));
  }

  decreaseOptions = (deviceIdentification, optionIdentification, quantity) => {
    return Promise.resolve(this.props.onDecreaseOptions(deviceIdentification, optionIdentification, quantity));
  }

  addSubAccessories = (deviceIdentification, accessoryIdentification, subAccessoryIdentification, quantity) => {
    return Promise.resolve(this.props.onAddSubAccessories(deviceIdentification, accessoryIdentification, subAccessoryIdentification, quantity));
  }

  decreaseSubAccessories = (deviceIdentification, accessoryIdentification, subAccessoryIdentification, quantity) => {
    return Promise.resolve(this.props.onDecreaseSubAccessories(deviceIdentification, accessoryIdentification, subAccessoryIdentification, quantity));
  }

  removeSubAccessories = (deviceIdentification, accessoryIdentification, subAccessoryIdentification) => {
    return Promise.resolve( this.props.onRemoveSubAccessories(deviceIdentification, accessoryIdentification, subAccessoryIdentification));
  }

  filterOptionCategories = (optionCategories) => {
    const result = {};

    Object.keys(optionCategories).forEach(key => {
      const optCategory = optionCategories[key];

      if (optCategory.isFinal) {
        // If status to filter is defined, show only accessories from the given status
        optCategory.options = optCategory.options.filter(opt => this.props.statusToFilter ? (opt.status === this.props.statusToFilter) : true);
        if (optCategory.options.length > 0) {
          result[key] = optCategory;
        }
      } else {
        optCategory.optionCategories = this.filterOptionCategories(optCategory.optionCategories);
        if (Object.keys(optCategory.optionCategories).length > 0) {
          result[key] = optCategory;
        }
      }
    });

    return result;
  }

  filterOptionCategoriesPayload = (optionCategoriesPayload) => {
    Object.keys(optionCategoriesPayload).forEach(optionCategoryKey => {
      optionCategoriesPayload[optionCategoryKey] = optionCategoriesPayload[optionCategoryKey].filter((optionCategoryItem) => {
        optionCategoryItem.optionCategories = this.filterOptionCategories(optionCategoryItem.optionCategories);
        return Object.keys(optionCategoryItem.optionCategories).length > 0;
      });
    });

    return optionCategoriesPayload;
  }

  retrieveOptions = (viewDefaultAccessory = false) => {
    this.updateStateIfMounted({optionsLoading: true});
    Promise.resolve(this.props.getOptions()).then((value) => {

      const filteredOptionCategories = this.filterOptionCategoriesPayload(value);

      if (viewDefaultAccessory && this.props.defaultModalAccessoryToView) {
        this.updateStateIfMounted({breadcrumb: this.computeBreadcrumbForDefaultOptionToView(filteredOptionCategories, this.props.defaultModalAccessoryToView?.optionIdentification?.id)});
        if (this.props.defaultOptionTypes) {
          const stateCopy = {};
          this.props.defaultOptionTypes.forEach((optType) => {
            stateCopy[optType.key] = true;
          });
          this.updateStateIfMounted({selectedOptionTypes: stateCopy});
        }

        // Scroll to the desired element (setTimeout is required to trigger the scroll after the first React render, which will initialize the scrollbars references)
        // and put elements in the page.
        setTimeout(() => {
          const elementToScroll = document.getElementById(`${this.props.defaultModalAccessoryToView.id}-option-modal-category`);
          if (elementToScroll && this.scrollbarsElement && this.scrollbarsElement.view) {
            this.scrollbarsElement.view.scrollTop = elementToScroll.offsetTop || 0;
          }
        }, 0);
      }

      this.updateStateIfMounted({optionCategories: filteredOptionCategories, optionsLoading: false});
    })
    .catch((err) => {
      console.log('Failed to retrieve the options');
      console.error(err);
    })
    .finally(() => {
      this.updateStateIfMounted({optionsLoading: false});
    });
  }

  componentWillUnmount = () => {
    this._isMounted = false;
  }

  updateStateIfMounted = (newState) => {
    if (this._isMounted) {
      this.setState(newState);
    }
  }

  renderTypeTabs = () => {
    return (
      <CategoryTabsWrapper id="options-modal-categoryTabsWrapper" mobileBreakpoint={this.props.mobileBreakpoint}>
        <CategoryTab id={'options-modal-accessories-tab'} primaryColor={this.props.primaryColor} secondaryColor={this.props.secondaryColor} onClick={() => { this.setState({activeCategory: active_category_type.accessories, breadcrumb: []}); }} active={active_category_type.accessories === this.state.activeCategory} disabled={(this.state.optionCategories?.accessories || []).length === 0} preventNavigation={this.props.disablePreviousOnEdit}>
          <TabLabel>
            {this.props.getLabel(active_category_to_label[active_category_type.accessories])}
          </TabLabel>
        </CategoryTab>
        <CategoryTab id={'options-modal-services-tab'}  primaryColor={this.props.primaryColor} secondaryColor={this.props.secondaryColor} onClick={() => { this.setState({activeCategory: active_category_type.services, breadcrumb: []}); }} active={active_category_type.services === this.state.activeCategory} disabled={(this.state.optionCategories?.services || []).length === 0} preventNavigation={this.props.disablePreviousOnEdit}>
        <TabLabel>
          {this.props.getLabel(active_category_to_label[active_category_type.services])}
        </TabLabel>
        </CategoryTab>
      </CategoryTabsWrapper>
    );
  }

  extractCurrentOptionCategories = (slotCategories) => {
    let categories = [];
    (slotCategories || []).forEach(slotCategory => {
      let currentCategory = slotCategory;
      for (const breadcrumbItem of this.state.breadcrumb) {
        currentCategory = currentCategory.optionCategories[breadcrumbItem.key];
      }
      if (currentCategory.isFinal) {
        currentCategory.options.forEach((opt) => { categories.push({
          optionIdentification: opt.optionIdentification,
          id: opt.id,
          getOptionDescription: opt.getOptionDescription,
          imageUrl: opt.imageUrl,
          canAdd: opt.canAdd,
          canRemove: opt.canRemove,
          canIncrease: opt.canIncrease,
          canDecrease: opt.canDecrease,
          quantity: opt.quantity,
          disabledMessage: opt.disabledMessage,
          status: opt.status,
          statusToolTipMessage: opt.statusToolTipMessage,
          hideStatusTooltip: opt.hideStatusTooltip,
          optionTypes: opt.optionTypes,
          subAccessories: opt.subAccessories,
          slotsConsumed: opt.slotsConsumed,
          isFinal: true}
          );
        });
      } else {
        Object.values(currentCategory.optionCategories).forEach((category) => categories.push({...category, isFinal: false}));
      }
    });
    return categories;
  }

  renderBreadcrumb = () => {
    return (
      <BreadcrumbWrapper mobileBreakpoint={this.props.mobileBreakpoint}>
        <BreadcrumbLink disabled={this.props.disablePreviousOnEdit} onClick={() => { this.updateStateIfMounted({breadcrumb: [], selectedOptionTypes: {}}); }}>
          {this.props.getLabel(active_category_to_label[this.state.activeCategory])}
        </BreadcrumbLink>
        {this.state.breadcrumb.map((breadcrumbItem, idx) => {
          return (
            <BreadcrumbItem mobileBreakpoint={this.props.mobileBreakpoint} key={idx}>
              <BreadcrumSeparator>{'>'}</BreadcrumSeparator>
              <BreadcrumbLink disabled={this.props.disablePreviousOnEdit} isLast={idx === (this.state.breadcrumb.length - 1)} onClick={() => { this.updateStateIfMounted({breadcrumb: [...this.state.breadcrumb].splice(0, idx + 1), selectedOptionTypes: {}}); }}>{breadcrumbItem.label}</BreadcrumbLink>
            </BreadcrumbItem>
          );
        })}
      </BreadcrumbWrapper>
    );
  }

  extractCategoryInformations = (category) => {
    let nbAccessoriesSelected = 0;
    let hasRecommended = false;
    const categoryStack = [category];

    // Unfold the stack
    while (categoryStack.length > 0) {
      const categoryItem = categoryStack.pop();

      if (categoryItem.options) {
        for (const optCategoryKey of Object.keys(categoryItem.options)) {
          const optionCategory = categoryItem.options[optCategoryKey];
          categoryStack.push(optionCategory);
        }
      } else {
        if (categoryItem.status === 'recommanded') {
          hasRecommended = true;
        }
        nbAccessoriesSelected += categoryItem.quantity || 0;
        if (categoryItem.subAccessories) {
          for (const subAccessory of Object.values(categoryItem.subAccessories)) {
            nbAccessoriesSelected += subAccessory.quantity || 0
          }
        }
      }
    }

    return {nbAccessoriesSelected, hasRecommended};
  }

  renderCategoryCardTopMessage = (category) => {
    let content = (<></>);
    let title;
    if (category.warningMessage) {
      title = category.warningMessage;
      content = (
        <>
          <WarningIconWrapper>
            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 1024 1024"><path fill="currentColor" d="M512 128c212 0 384 172 384 384S724 896 512 896 128 724 128 512s172-384 384-384zm-32 150c-8 8-11 20-11 31l11 249c0 9 3 18 10 24 6 5 14 8 22 8 9 0 17-3 23-8 6-6 10-15 9-24l13-249c0-12-5-23-13-31-9-9-20-13-32-13s-24 4-32 13zm67 469v-1c10-9 16-22 15-36 0-13-5-26-15-35-9-9-22-15-35-14-14-1-26 5-36 14-9 9-15 22-14 35-1 14 5 28 15 37 20 18 50 18 70 0z"/></svg>
          </WarningIconWrapper>
          <WarningLabel title={category.warningMessage}>{category.warningMessage}</WarningLabel>
        </>
      );
    }
    return <OptionCategoryMessageWrapper title={title}>{content}</OptionCategoryMessageWrapper>;
  }

  renderTooltipContent = (tooltipMessage) => {
    return (
      <TooltipContent>
        <TooltipLabel>
          {tooltipMessage}
        </TooltipLabel>
      </TooltipContent>
    );
  }

  renderRecommandedStatus = (tooltipMessage) => {
    const recommandedStatusComponent = (<StatusTooltip fixed={true} getLabel={this.props.getLabel} status={'recommanded'}></StatusTooltip>);

    if (tooltipMessage) {
      return (<Tooltip
        boundingElement={this.scrollbarsWrapperElement}
        toggleElement={recommandedStatusComponent}
        containerElement={this.renderTooltipContent(tooltipMessage)}
        allowTop={true}>
      </Tooltip>);
    }

    return recommandedStatusComponent;
  }

  renderCategory = (category) => {
    const {nbAccessoriesSelected, hasRecommended} = this.extractCategoryInformations(category);
    return (
      <CategoryWrapper id={`${category.categoryKey}-option-modal-category`} mobileBreakpoint={this.props.mobileBreakpoint} color={this.props.secondaryColor} onClick={() => { this.updateStateIfMounted({breadcrumb: [...this.state.breadcrumb, {key: category.categoryKey, label: category.categoryLabel}], selectedOptionTypes: {}}); }}>
        {this.renderCategoryCardTopMessage(category)}
        <CategoryTitleAndStatusWrapper>
          <CategoryTitle>{category.categoryLabel}</CategoryTitle>
          {hasRecommended && this.renderRecommandedStatus(category.statusToolTipMessage)}
        </CategoryTitleAndStatusWrapper>
        {category.categoryDescription && <CategoryDescription>{category.categoryDescription}</CategoryDescription>}
        {nbAccessoriesSelected > 0 && <NumberOfAccessoriesWrapper>
          <StatusTooltip fixed={true} getLabel={this.props.getLabel} message={this.props.getAndReplaceLabel('options_screen.number_accessories_added', nbAccessoriesSelected, 'nb')}></StatusTooltip>
        </NumberOfAccessoriesWrapper>}
        <TopBorder></TopBorder>
      </CategoryWrapper>
    );
  }

  raiseNotificationEvent = (eventType, option, quantity) => {
    document.dispatchEvent(new CustomEvent('iac-add-to-notification-feed', {
      bubbles: true,
      detail: {
        identificationKey: this.props.deviceIdentification.identificationKey,
        status: eventType,
        type: option.type,
        id: option.id,
        slot: option.slot,
        quantity
      }
    }));
  }

  renderActionButton = (option, addDisabled) => {
    return (
      <ActionButtonWrapper>
        {option.quantity > 0 && <QuantityEditor id={option.id} primaryColor={this.props.primaryColor} primaryTextColor={this.props.primaryTextColor} isSaving={this.props.isSaving} quantity={option.quantity} canIncrease={option.canIncrease}
          onIncrease={() => {
            this.addOptions(this.props.deviceIdentification, option.optionIdentification, 1).then(() => {
              this.raiseNotificationEvent('added', option, 1);
              this.retrieveOptions(false);
            });
            }}
          canDecrease={option.canDecrease && (option.quantity > 1 || (option.quantity === 1 && option.canRemove))}
          onDecrease={() => {
            if (option.quantity === 1 && option.canRemove && this.props.onRemoveOptions) {
              this.removeOptions(this.props.deviceIdentification, option.optionIdentification).then(() => {
                this.raiseNotificationEvent('removed', option, 1);
                this.retrieveOptions(false);
              });
            } else {
              if (this.props.onDecreaseOptions) {
                this.decreaseOptions(this.props.deviceIdentification, option.optionIdentification, 1).then(() => {
                  this.raiseNotificationEvent('removed', option, 1);
                  this.retrieveOptions(false);
                });
              }
            }
            }}></QuantityEditor>}
        {(option.canRemove && option.quantity > 0) && <IconButton id={`${option.id}-option-modal-remove`} icon="remove" size={40} padding={7} color={'#626469'} disabled={this.props.isSaving} onClick={() => { this.removeOptions(this.props.deviceIdentification, option.optionIdentification).then(() => {
          this.raiseNotificationEvent('removed', option, option.quantity);
          this.retrieveOptions(false);
        }); }}></IconButton>}
        {option.quantity === 0 && <Button id={`${option.id}-option-modal-add`} disabled={addDisabled} label={this.props.getLabel('app.action.add')} title={option.disabledMessage} icon={'plus-large'} onClick={() => {
          if (!addDisabled) {
            this.addOptions(this.props.deviceIdentification, option.optionIdentification, 1).then(() => {
              this.raiseNotificationEvent('added', option, 1);
              this.retrieveOptions(false);
            });
          }
          }}>
        </Button>}
      </ActionButtonWrapper>
    );
  }

  renderOptionSlots = (option) => {
    return (
      <OptionSlotsWrapper>
        <SlotsTitleAndLineWrapper>
          <OptionSlotsLabel>{this.props.getLabel('options_screen.slot_consumed')}</OptionSlotsLabel>
          <OptionSlotsLine></OptionSlotsLine>
        </SlotsTitleAndLineWrapper>
        <SlotRow>
          {option.slotsConsumed.map((slot, idx) => {
            return (
              <SlotWrapper key={idx}>
                <SlotChip
                  label={slot.label}
                  filled={slot.filled}
                  disabled={slot.disabled}
                  onClick={() => { if (slot.onClick) { Promise.resolve(slot.onClick()).then(() => {
                    this.retrieveOptions(false);
                  });} }}
                  fillColor={this.props.primaryColor}
                  fillTextColor={this.props.primaryTextColor}
                ></SlotChip>
              </SlotWrapper>);
            }
          )}
        </SlotRow>
      </OptionSlotsWrapper>
    );
  }

  renderOptionCategory = (option) => {
    const addDisabled = this.props.isSaving || !option.canAdd;
    const description = option.getOptionDescription();
    return (
      <OptionWrapper id={`${option.id}-option-modal-category`} mobileBreakpoint={this.props.mobileBreakpoint}>
        <OptionContent>
          <OptionImage src={option.imageUrl}></OptionImage>
          <CategoryDescriptionAndActionsWrapper>
            <OptionCol>
              <OptionLinkWrapper>
                <LinkWrapper>
                  {this.props.renderProductLink(option.id)}
                </LinkWrapper>
                {(option.status === 'recommanded' && !option.hideStatusTooltip) && this.renderRecommandedStatus(option.statusToolTipMessage)}
              </OptionLinkWrapper>
              <DescriptionWrapper>
                <OptionDescription title={description}>{description}</OptionDescription>
              </DescriptionWrapper>
            </OptionCol>
            <OptionRow>
              {this.renderActionButton(option, addDisabled)}
            </OptionRow>
          </CategoryDescriptionAndActionsWrapper>
        </OptionContent>
        {option.slotsConsumed && option.slotsConsumed.length > 0 && this.renderOptionSlots(option)}
        {option.subAccessories && Object.keys(option.subAccessories).length > 0 && <SubAccessoriesWrapper>
          <SubAccessoriesManager
            deviceIdentification={this.props.deviceIdentification}
            accessoryIdentification={option.optionIdentification}
            option={option}
            primaryColor={this.props.primaryColor}
            primaryTextColor={this.props.primaryTextColor}
            isSaving={this.props.isSaving}
            renderProductLink={this.props.renderProductLink}
            subAccessories={option.subAccessories}
            getLabel={this.props.getLabel}
            getAndReplaceLabel={this.props.getAndReplaceLabel}
            mobileBreakpoint={this.props.mobileBreakpoint}
            onAddSubAccessories={this.addSubAccessories}
            onDecreaseSubAccessories={this.decreaseSubAccessories}
            onRemoveSubAccessories={this.removeSubAccessories}
            raiseNotificationEvent={this.raiseNotificationEvent}
            retrieveOptions={() => { this.retrieveOptions(false); }}
          >
          </SubAccessoriesManager>
        </SubAccessoriesWrapper>}
      </OptionWrapper>
    );
  }

  extractCategoriesToShow = () => {
    let categoriesToShow;
    if (active_category_type.accessories === this.state.activeCategory) {
      categoriesToShow = this.extractCurrentOptionCategories(this.state.optionCategories?.accessories).filter(optCategory => {
        let showCategory = true;
        if (optCategory.optionTypes && Object.keys(this.state.selectedOptionTypes).length > 0) {
          let atLeastOneCategoryFound = false;
          for (let i = 0; i < optCategory.optionTypes.length && !atLeastOneCategoryFound;i++) {
            if (this.state.selectedOptionTypes[optCategory.optionTypes[i].key]) {
              atLeastOneCategoryFound = true;
            }
          }
          showCategory = atLeastOneCategoryFound;
        }
        return showCategory;
      });
    } else if (active_category_type.services === this.state.activeCategory) {
      categoriesToShow = this.extractCurrentOptionCategories(this.state.optionCategories?.services).filter(optCategory => {
        let showCategory = true;
        if (optCategory.optionTypes && Object.keys(this.state.selectedOptionTypes).length > 0) {
          let atLeastOneCategoryFound = false;
          for (let i = 0; i < optCategory.optionTypes.length && !atLeastOneCategoryFound;i++) {
            if (this.state.selectedOptionTypes[optCategory.optionTypes[i].key]) {
              atLeastOneCategoryFound = true;
            }
          }
          showCategory = atLeastOneCategoryFound;
        }
        return showCategory;
      });
    }
    return categoriesToShow;
  }

  /**
   * Sort the option categories, option categories with a recommanded options should appear first.
   */
  sortOptionCategories = (categories) => {
    const result = [];
    const resultWithoutRecommanded = [];

    categories.forEach(cat => {
      const categoryInformations = this.extractCategoryInformations(cat);
      if (categoryInformations.hasRecommended) {
        result.push(cat);
      } else {
        resultWithoutRecommanded.push(cat);
      }
    });

    return [...result, ...resultWithoutRecommanded];
  }

  renderOptionsContent = (categories) => {
    return (<>
      {(this.state.optionsLoading && Object.keys(this.state.optionCategories).length === 0) ? <Skeleton mobileBreakpoint={this.props.mobileBreakpoint} nbCols={6}></Skeleton> :
      this.sortOptionCategories(categories).map((category,idx) =>
        <Fragment key={idx}>{category.isFinal ? this.renderOptionCategory(category) : this.renderCategory(category)}
        </Fragment>
      )}
    </>);
  }

  // Extract the distinct option types. We get rid of the duplicate option types keys.
  extractOptionTypes = () => {
    const optionTypes = {};

    const optionCategories = this.extractCurrentOptionCategories(this.optionsToExplore(this.state.optionCategories));

    optionCategories.forEach(optCategory => {
      (optCategory.optionTypes || []).forEach(optType => {
        optionTypes[optType.key] = optType;
      });
    });

    return Object.values(optionTypes);
  }

  renderOptionTypes = (optionTypes) => {
    return (
      <>
        <SelectOptionTypeTitle>{this.props.getLabel('options_screen.select_type')}</SelectOptionTypeTitle>
        <OptionTypesWrapper mobileBreakpoint={this.props.mobileBreakpoint}>
          <Scrollbars>
            <div style={{display: 'flex' }}>
              {optionTypes.map((optType, idx) => {
                const selected = this.state.selectedOptionTypes[optType.key] === true;
                return (
                <ChipWrapper key={idx}>
                  <Chip
                  disabled={false}
                  title={optType.label}
                  value={optType.key}
                  selected={selected}
                  onClick={() => {
                    const stateCopy = {...this.state.selectedOptionTypes};
                    if (selected) {
                      delete stateCopy[optType.key];
                    } else {
                      stateCopy[optType.key] = true;
                    }
                    this.updateStateIfMounted({selectedOptionTypes: stateCopy});
                  }}
                  ></Chip>
                </ChipWrapper>);
              })}
            </div>
          </Scrollbars>
        </OptionTypesWrapper>
      </>
    );
  }

  renderSlotCapacities = (hasFinal) => {
    const slots = this.extractSlots();
    return (<>{slots.length > 0 && <SlotCapacities getLabel={this.props.getLabel} slots={slots} fillColor={this.props.primaryColor} fillTextColor={this.props.primaryTextColor} forceDisable={!hasFinal}></SlotCapacities> }</>);
  }

  setScrollbarsWrapperRef = element => {
    this.scrollbarsWrapperElement = element;
  }

  setScrollbarsRef = element => {
    this.scrollbarsElement = element;
  };

  render() {

    const optionTypes = this.extractOptionTypes();
    const categoriesToShow = this.extractCategoriesToShow();

    // Check if we have a final
    const hasFinal = categoriesToShow.filter(cat => cat.isFinal).length > 0;

    return (
      <ModalContainer id="options-modal-modalContainer">
        <Container id="options-modal-Container" mobileBreakpoint={this.props.mobileBreakpoint}>
          <DesktopNotifcationWrapper>
            <ModalNotification></ModalNotification>
          </DesktopNotifcationWrapper>
          <HeadWrapper id="options-modal-headWrapper" mobileBreakpoint={this.props.mobileBreakpoint}>
            <HeadContainerWrapper id="options-modal-headContainerWrapper">
            <ImageAndHeadWrapper>
              <ItemImage src={this.props.imageUrl}></ItemImage>
              <Col>
                <TitleAndCloseWrapper>
                  <TitleWrapper>
                    <TitleType>{this.props.titleType}</TitleType>
                    <TitleType>:</TitleType>
                    <TitleLinkWrapper>
                      {this.props.renderProductLink(this.props.moduleId)}
                    </TitleLinkWrapper>
                  </TitleWrapper>
                    <CloseIcon onClick={(e) => {
                      e.preventDefault();
                      this.props.closeModal();
                    }}>
                      <MsxIcons name="close"></MsxIcons>
                    </CloseIcon>
                </TitleAndCloseWrapper>
                {!this.state.optionsLoading && this.renderSlotCapacities(hasFinal)}
                {(this.state.activeCategory != null) && this.renderBreadcrumb()}

              </Col>


            </ImageAndHeadWrapper>
            </HeadContainerWrapper>


            {this.renderTypeTabs()}
          </HeadWrapper>
          <ContentWrapper mobileBreakpoint={this.props.mobileBreakpoint} ref={this.setScrollbarsWrapperRef}>
            <MobileNotificationWrapper>
              <ModalNotification></ModalNotification>
            </MobileNotificationWrapper>
            {this.state.breadcrumb.length > 0 && <ContentCategoryTitle>{this.state.breadcrumb[this.state.breadcrumb.length - 1].label}</ContentCategoryTitle>}
            {optionTypes.length > 0 && this.renderOptionTypes(optionTypes)}
            <Scrollbars ref={this.setScrollbarsRef}>
              <ScrollbarsContent>
                <OptionsContent mobileBreakpoint={this.props.mobileBreakpoint}>
                  {this.renderOptionsContent(categoriesToShow)}
                </OptionsContent>
              </ScrollbarsContent>
            </Scrollbars>
          </ContentWrapper>
          {this.state.breadcrumb.length > 0 && !this.props.disablePreviousOnEdit && <ButtonWrapper>
            <Button id={'options-modal-previous-button'} color={this.props.primaryColor} textColor={this.props.primaryTextColor} label={this.props.getLabel('app.action.previous')} onClick={() => {
              this.updateStateIfMounted({breadcrumb: [...this.state.breadcrumb].slice(0, -1), selectedOptionTypes: {}});
            }}></Button>
          </ButtonWrapper>}
        </Container>
        <NotificationFeed
          moduleId={this.props.moduleId}
          primaryColor={this.props.primaryColor}
          primaryTextColor={this.props.primaryTextColor}
          isSaving={this.props.isSaving}
          renderProductLink={this.props.renderProductLink}
          getLabel={this.props.getLabel}
          getAndReplaceLabel={this.props.getAndReplaceLabel}
          mobileBreakpoint={this.props.mobileBreakpoint}></NotificationFeed>
      </ModalContainer>
    );
  }
}

export default OptionsModal;
